import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
} from "@mui/material";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialButton from "../../../components/MaterialButton";
import MaterialInput from "../../../components/MaterialInput";
import AddIcon from "@mui/icons-material/Add";
import {
  deleteData,
  fetchData,
  fetchDataById,
  postData,
  putData,
} from "../../Utils/Baseurl";
import Swal from "sweetalert2";
import SimpleTable from "../../../components/SimpleTable";
import { useSnackbar } from "../../../components/SnackbarProvider";
import { useLoading } from "../../../context/loadingcontext";
import { useAuth } from "../../../context/AuthContext";
import CanAccess from "../../../components/permission/CanAccess";

const RoleCreate = () => {
  const { rolePermission } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [list, setList] = useState([]);
  const [pages, setPages] = useState({
    view: false,
    edit: false,
    id: "",
  });
  const [role, setRole] = useState({
    name: "",
    nameStatus: "unchecked",
    description: "",
    descriptionStatus: "unchecked",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { setCirleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    getRoleList();
  }, []);
  //   Get Role List

  const getRoleList = async () => {
    setIsLoading(true);
    try {
      const response = await fetchData("api/roles/");

      if (response?.status && Array.isArray(response.data)) {
        setList(response.data);
      } else {
        setList([]);
      }
    } catch (error) {
      console.error("Error while fetching role list:", error);
      setList([]);
    } finally {
      setIsLoading(false);
    }
  };

  //   Create or Update Role

  const createRole = async () => {
    const data = {
      role: role?.name,
      description: role?.description,
    };

    try {
      let response;
      if (pages.edit) {
        response = await putData(`api/roles/${pages?.id}/`, data);
      } else {
        response = await postData("api/roles/", data);
      }
      if (response.status) {
        showSnackbar(response?.message, "success");
        handleDialogClose();
        getRoleList();
      } else {
        showSnackbar(response?.message, "error");
      }
    } catch (error) {
      console.log(error);

      let message = "An unexpected error occurred";

      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }

      showSnackbar(message, "error");
      handleDialogClose();
    }
  };

  //   View Role for Read-Only

  const viewRole = async (row) => {
    setCirleLoading(true);
    setPages({
      view: true,
      edit: false,
    });

    try {
      const response = await fetchDataById(`api/roles/${row}/`);
      handleDialogOpen();

      if (response.status) {
        setRole((prev) => ({
          ...prev,
          name: response?.data?.role,
          description: response?.data?.description,
        }));
      }
    } catch (error) {
      console.error("Error fetching Role data:", error);
    } finally {
      setCirleLoading(false);
    }
  };

  //   Delete Role with Confirmation

  const deleteRole = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure you want to delete this Role?",
      text: "This action is irreversible and cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setCirleLoading(true);
      try {
        const response = await deleteData(`api/roles/${row.id}/`);

        if (response.status) {
          showSnackbar(response?.message, "success");
          getRoleList();
        } else {
          showSnackbar(response?.message, "error");
        }
      } catch (error) {
        let message = "An unexpected error occurred";
        if (error?.response?.data?.message) {
          message = error.response.data.message;
        } else if (error?.message) {
          message = error.message;
        }

        showSnackbar(message, "error");
      } finally {
        setCirleLoading(false);
      }
    }
  };

  //Edit Function call

  const editRole = async (row) => {
    handleDialogOpen();
    setCirleLoading(true);
    setPages({
      view: false,
      edit: true,
      id: row,
    });
    try {
      const response = await fetchDataById(`api/roles/${row}/`);

      if (response.status) {
        setRole((prev) => ({
          ...prev,
          name: response?.data?.role,
          description: response?.data?.description,
          nameStatus: "success",
        }));
      }
    } catch (error) {
      console.error("Error fetching Role data:", error);
    } finally {
      setCirleLoading(false);
    }
  };

  //   Handle Input Change
  const handleChange = (event) => {
    let roleState = { ...role };
    switch (event.target.name) {
      case "description": {
        const alphaNumberLengthRegex = new RegExp(
          "^[a-zA-Z0-9 !@#$%^&*()_+{}|:\"<>?`\\[\\]\\\\;,./'-=~ ]{1,250}$"
        );
        if (alphaNumberLengthRegex.test(event.target.value?.trim())) {
          roleState[event.target.name + "Status"] = "success";
        } else {
          roleState[event.target.name + "Status"] =
            event.target.value === "" ? "unchecked" : "error";
        }
        break;
      }
      case "name": {
        const alphaNumberLengthRegex = new RegExp("^([a-zA-Z0-9 ]{1,35})$");
        if (alphaNumberLengthRegex.test(event.target.value?.trim())) {
          roleState[event.target.name + "Status"] = "success";
        } else {
          roleState[event.target.name + "Status"] =
            event.target.value === "" ? "unchecked" : "error";
        }
        break;
      }
      default:
        break;
    }
    roleState[event.target.name] = event.target.value;
    setRole(roleState);
  };

  //   Dialog Control Handlers

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPages({});
    setRole({});
  };

  //   Table Column & Row Configuration
  const RoleData = {
    columns: [
      {
        key: "names",
        header: "Role Name",
      },
      { header: "ACTION", key: "action" },
    ],
    row: list?.map((item) => ({
      id: item?.id,
      names: item?.role.replace(/_/g, " "),
    })),
  };
  // role permission for table

  const actionVisibility = {
    showEdit: CanAccess.can(rolePermission, "Role", "Role Edit"),
    showView: CanAccess.can(rolePermission, "Role", "Role View"),
    showDelete: CanAccess.can(rolePermission, "Role", "Role Delete"),
  };

  return (
    <DashboardLayout>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        p={1}
        mb={2}
      >
        <Stack direction="column" spacing={0}>
          <MaterialTypography variant="h5" fontWeight="medium">
            Role Master
          </MaterialTypography>
          <MaterialTypography variant="caption" color="text">
            You can view the Role list here.
          </MaterialTypography>
        </Stack>
        {/* <CanAccess module="Role" component="Role Add"> */}
        <MaterialButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleDialogOpen}
        >
          Add Role
        </MaterialButton>
        {/* </CanAccess> */}
      </Stack>
      <SimpleTable
        columns={RoleData?.columns}
        data={RoleData?.row}
        onEdit={editRole}
        onDelete={deleteRole}
        onView={viewRole}
        isLoading={isLoading}
        actionVisibility={actionVisibility}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="new-employee-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            width: "600px",
            maxWidth: "90%",
          },
        }}
      >
        <DialogTitle id="new-employee-dialog-title">
          {pages?.view ? "View Role" : pages?.edit ? "Edit Role" : "Add Role"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="column" spacing={1}>
                <MaterialTypography variant="caption" color="text">
                  Role <span style={{ color: "red" }}>*</span>
                </MaterialTypography>
                <MaterialInput
                  name="name"
                  placeholder="Enter Role Name"
                  value={role?.name}
                  onChange={handleChange}
                  error={role.nameStatus === "error"}
                  success={role.nameStatus === "success"}
                  disabled={pages.view}
                />
                {role.nameStatus === "error" && (
                  <FormControl error style={{ marginTop: "0px" }}>
                    <FormHelperText>
                      Role can only contain letters, numbers, and spaces, and
                      must be 35 characters or fewer.
                    </FormHelperText>
                  </FormControl>
                )}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="column" spacing={1}>
                <MaterialTypography variant="caption" color="text">
                  Description
                </MaterialTypography>
                <MaterialInput
                  name="description"
                  multiline
                  rows={4}
                  placeholder="Enter Description"
                  value={role?.description}
                  onChange={handleChange}
                  error={role.descriptionStatus === "error"}
                  success={role.descriptionStatus === "success"}
                  disabled={pages.view}
                />
                {role.descriptionStatus === "error" && (
                  <FormControl error style={{ marginTop: "0px" }}>
                    <FormHelperText>
                      Description can only contain letters, numbers, spaces, and
                      common special characters, and must be 250 characters or
                      fewer
                    </FormHelperText>
                  </FormControl>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MaterialButton onClick={handleDialogClose} color="primary">
            Cancel
          </MaterialButton>
          {!pages?.view && (
            <MaterialButton
              onClick={createRole}
              color="dark"
              variant="contained"
            >
              Save
            </MaterialButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default RoleCreate;
